// React glue between graphFocus (the request store) and the ReactFlow canvases.
//
// Two hooks, because serving a focus request takes two different actors:
//
//   useFocusDrill  — the EDITOR shell watches for a request aimed at a
//                    container it hosts and opens that container (drill into a
//                    graph group / state machine). Only it knows how.
//   useCanvasFocus — the CANVAS that ends up holding the target centres on it
//                    and marks the request served.
//
// Split this way because the two never live in the same component: the canvas
// for a container does not exist until the shell has drilled in. Merging them
// would mean a canvas trying to mount itself.

import { useEffect } from 'react'
import { useReactFlow } from '@xyflow/react'
import { getPendingFocus, subscribeFocus, completeFocus, GRAPH_KEYS } from './graphFocus.js'

/** Run `fn(pending)` now and on every change — the request may predate the mount. */
function useFocusRequest(fn, deps) {
  useEffect(() => {
    fn(getPendingFocus())
    return subscribeFocus(fn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * Centre this canvas on the pending target, if it is ours and we hold it.
 *
 * @param {object} opts
 * @param {'body'|'face'|'event'} opts.graph
 * @param {string|null} [opts.containerId]  the group/SM/function this canvas shows
 *        the inside of; null for a top-level canvas. A request is served by the
 *        canvas whose scope matches EXACTLY — otherwise a top-level canvas would
 *        answer for a node it merely contains a container for, and the user
 *        would land on the group box instead of the node.
 * @param {number} [opts.zoom]
 */
export function useCanvasFocus({ graph, containerId = null, zoom = 1.1, onSelectNode = null } = {}) {
  const { getNode, setCenter } = useReactFlow()
  useFocusRequest((pending) => {
    if (!pending || pending.graph !== GRAPH_KEYS[graph]) return
    if ((pending.container?.id ?? null) !== containerId) return

    if (pending.point) {
      setCenter(pending.point.x, pending.point.y, { zoom, duration: 350 })
      completeFocus(pending.seq)
      return
    }
    const node = getNode(pending.nodeId)
    if (!node) return // not ours — another canvas may hold it, or it is gone
    setCenter(
      node.position.x + (node.measured?.width ?? 200) / 2,
      node.position.y + (node.measured?.height ?? 80) / 2,
      { zoom, duration: 350 },
    )
    // Centring alone leaves you to work out WHICH of the nodes now on screen you
    // asked for — and the rail and Ctrl+F exist precisely because several look
    // alike. Select it, so the answer is pointed at rather than merely framed.
    //
    // Through the CANVAS's own change handler, not useReactFlow().setNodes: every
    // canvas here is controlled (`nodes` prop + onNodesChange), so a write
    // straight to the ReactFlow store survives only until the owning component
    // next renders — which looks like it works under HMR and silently does
    // nothing on a cold load.
    onSelectNode?.(pending.nodeId)
    completeFocus(pending.seq)
  }, [graph, containerId, zoom, getNode, setCenter, onSelectNode])
}

/**
 * The `select` changes that leave exactly `nodeId` selected.
 *
 * ReactFlow's own vocabulary, so it lands through onNodesChange like a click
 * would and the owning useNodesState stays the single source of truth. Emits
 * only the nodes whose state actually flips.
 */
export function selectOnlyChanges(nodes, nodeId) {
  return (nodes ?? [])
    .filter((n) => !!n.selected !== (n.id === nodeId))
    .map((n) => ({ id: n.id, type: 'select', selected: n.id === nodeId }))
}

/**
 * Drill into the container a pending request points at.
 *
 * @param {object} opts
 * @param {'body'|'face'|'event'} opts.graph
 * @param {(containerId: string, pending: object) => void} opts.onOpenContainer
 * @param {() => string|null} [opts.currentContainerId] what is open right now,
 *        so a request for a node at the TOP level pops back out of whatever
 *        container the user left open — otherwise the request can never be
 *        served and times out while the editor sits inside the wrong sub-graph.
 */
export function useFocusDrill({ graph, onOpenContainer, currentContainerId }) {
  useFocusRequest((pending) => {
    if (!pending || pending.graph !== GRAPH_KEYS[graph]) return
    const want = pending.container?.id ?? null
    const have = currentContainerId?.() ?? null
    if (want === have) return
    onOpenContainer(want, pending)
  }, [graph, onOpenContainer, currentContainerId])
}
